import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import {
  deleteSystemRole,
  getSystemRole,
  getSystemRoleImpact,
  getSystemRolePermissions,
  listSystemRoles,
  setSystemRolePermissions,
  upsertSystemRole,
} from "../services/systemRoleService";
import { useSystemRoleStore } from "../stores/systemRoleStore";
import type {
  DeleteSystemRoleRequest,
  SetPermissionsRequest,
  UpsertSystemRoleRequest,
} from "../types/systemRole.types";

export const useSystemRoleList = () =>
  useQuery({
    queryKey: ["systemRoles"],
    queryFn: listSystemRoles,
    staleTime: 5_000,
  });

export const useSystemRoleDetail = (roleGuid: string | null) =>
  useQuery({
    queryKey: ["systemRole", roleGuid],
    queryFn: () => getSystemRole(roleGuid!),
    enabled: !!roleGuid,
    gcTime: 0,
  });

export const useRolePermissions = (roleGuid: string | null) =>
  useQuery({
    queryKey: ["systemRole", roleGuid, "permissions"],
    queryFn: () => getSystemRolePermissions(roleGuid!),
    enabled: !!roleGuid,
    gcTime: 0,
  });

export const useRoleImpact = (roleGuid: string | null) =>
  useQuery({
    queryKey: ["systemRole", roleGuid, "impact"],
    queryFn: () => getSystemRoleImpact(roleGuid!),
    enabled: !!roleGuid,
    gcTime: 0,
  });

export const useUpsertSystemRole = () => {
  const qc = useQueryClient();

  return useMutation({
    mutationFn: (req: UpsertSystemRoleRequest) => upsertSystemRole(req),
    onSuccess: (data, variables) => {
      toast.success(
        variables.roleGuid
          ? "역할이 수정되었습니다."
          : "역할이 등록되었습니다.",
      );
      qc.invalidateQueries({ queryKey: ["systemRoles"] });
      if (data?.guid) {
        qc.invalidateQueries({ queryKey: ["systemRole", data.guid] });
      }
    },
    onError: (err: Error) => {
      toast.error(`저장 실패: ${err.message}`);
    },
  });
};

export const useSetSystemRolePermissions = () => {
  const qc = useQueryClient();

  return useMutation({
    mutationFn: (req: SetPermissionsRequest) => setSystemRolePermissions(req),
    onSuccess: (_data, variables) => {
      toast.success("권한 매핑이 저장되었습니다.");
      qc.invalidateQueries({
        queryKey: ["systemRole", variables.roleGuid, "permissions"],
      });
    },
    onError: (err: Error) => {
      toast.error(`저장 실패: ${err.message}`);
    },
  });
};

export const useDeleteSystemRole = () => {
  const qc = useQueryClient();
  const closeDrawer = useSystemRoleStore((s) => s.closeDrawer);
  const closeDelete = useSystemRoleStore((s) => s.closeDelete);

  return useMutation({
    mutationFn: (req: DeleteSystemRoleRequest) => deleteSystemRole(req),
    onSuccess: (data) => {
      toast.success(
        `역할이 삭제되었습니다. (회사 ${data.companyReplicaCount}개, 직원 ${data.assignedAccountCount}명 영향)`,
      );
      qc.invalidateQueries({ queryKey: ["systemRoles"] });
      closeDelete();
      closeDrawer();
    },
    onError: (err: Error) => {
      toast.error(`삭제 실패: ${err.message}`);
    },
  });
};
